import { useMutation } from "@tanstack/react-query";
import { saveForm, submitForm } from "../../api/endpoints/fetchTemplates";
import type { Template } from "../../api/endpoints/fetchTemplates";
type MinimalUser = { email?: string; role?: "admin" | "member" } | null;

export function useSaveFormMutation(params: {
  template: Template;
  user: MinimalUser;
  successTimerRef: React.MutableRefObject<number | null>;
  setShowSuccess: (v: boolean) => void;
  setErrorText: (v: string | null) => void;
  onSaved: (fileId: string) => void;
  onClose?: () => void;
}) {
  const { template, user, successTimerRef, setShowSuccess, setErrorText, onSaved, onClose } = params;

  return useMutation({
    networkMode: "always",
    mutationFn: async ({ payload, saveClicked }: { payload: unknown; saveClicked: boolean }) => {
      if (!template?.fileId) {
        throw new Error("Missing fileId for save operation");
      }
      const emailId = user?.email || "";
      if (saveClicked) {
        return saveForm(template.fileId, payload as Record<string, unknown>, emailId, template.memberId);
      } else {
        return submitForm(template.cacheId ?? template.fileId, payload as Record<string, unknown>, user?.role === "admin");
      }
    },
    onMutate: () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
        successTimerRef.current = null;
      }
      setShowSuccess(false);
      setErrorText(null);
    },
    onSuccess: () => {
      setShowSuccess(true);
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
      successTimerRef.current = window.setTimeout(() => {
        setShowSuccess(false);
        if (template?.fileId && typeof onSaved === "function") {
          onSaved(template.fileId);
        }
        if (onClose) onClose();
      }, 2000);
    },
    onError: (err: unknown) => {
      setShowSuccess(false);
      let message = "Failed to save";
      if (typeof err === "object" && err !== null) {
        const maybeWithResponse = err as { response?: { data?: { message?: string } } };
        const maybeWithMessage = err as { message?: string };
        message = maybeWithResponse.response?.data?.message ?? maybeWithMessage.message ?? message;
      }
      setErrorText(message);
    },
  });
}
